<template>
    <v-dialog
        v-model="show"
        persistent
        eager
        :width="displayWidth"
        @keydown.esc="onEscapeKeydown"
    >
        <v-card role="region" aria-label="card-upload-devices">
            <v-card-title role="region" aria-label="card-header" class="px-3">
                <v-container fluid pa-0>
                    <v-row no-gutters>
                        <v-col>
                            <div>{{ displayTitle }}</div>
                            <div
                                class="text-body-2 deep-grey--text text--lighten-1"
                            >
                                Map: {{ channelMap.name }}
                            </div>
                        </v-col>
                        <v-col cols="7">
                            <service-account-info
                                :id="serviceAccount.xboAccountId"
                                :name="serviceAccount.name.slice(0, 22)"
                                id-label="XBO Account Id"
                            ></service-account-info>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-title>
            <v-card-text class="scrollable-content pb-0">
                <div>
                    <v-text-field
                        dense
                        clearable
                        label="Quick filter"
                        class="pt-2"
                        placeholder="Type in a channel name or number"
                        :value="channelFilter"
                        :append-icon="icons.search"
                        :loading="isDataLoading"
                        :disabled="isDataLoading"
                        @input="onChannelFilterInput"
                    ></v-text-field>
                </div>
                <v-simple-table
                    fixed-header
                    class="listings-table elevation-5"
                    height="420px"
                >
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th class="px-3 select-col"></th>
                                <th class="channel-col">
                                    Channel
                                </th>
                                <th
                                    v-for="(header, h) in tableHeaders"
                                    :key="`time-header-${h}`"
                                    colspan="30"
                                    class="header-col"
                                >
                                    {{ header.start }} - {{ header.end }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, index) in filteredChannels"
                                :key="`row-${index}`"
                                class="selectable-row"
                            >
                                <td class="px-3">
                                    <v-checkbox
                                        color="accent"
                                        :value="item.number === selectedChannel"
                                        @change="
                                            onChannelSelectChange(
                                                $event,
                                                item.number
                                            )
                                        "
                                    ></v-checkbox>
                                </td>
                                <td>
                                    <div>
                                        <v-img
                                            :src="channelIcon(item.stationId)"
                                            :alt="item.shortName"
                                            height="40px"
                                            contain
                                        >
                                            <template v-slot:placeholder>
                                                <span>{{
                                                    item.shortName
                                                }}</span>
                                            </template>
                                        </v-img>
                                    </div>
                                    <div
                                        class="d-flex justify-center text-body-2 deep-grey--text font-weight-medium"
                                    >
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on }">
                                                <span v-on="on">{{
                                                    item.number
                                                }}</span>
                                            </template>
                                            <span>{{ item.shortName }}</span>
                                        </v-tooltip>
                                    </div>
                                </td>
                                <td
                                    v-for="(col,
                                    colIndex) in item.listings.filter(
                                        l => l.display
                                    )"
                                    :key="`listing-${colIndex}`"
                                    :colspan="col.span"
                                    class="listing-col px-2"
                                >
                                    <span v-if="col.span < 30">
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on }">
                                                <span v-on="on">{{
                                                    col.title
                                                }}</span>
                                            </template>
                                            <span>{{ col.title }}</span>
                                        </v-tooltip>
                                    </span>
                                    <span v-else>{{ col.title }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
                <v-select
                    v-model="selectedDevices"
                    label="Select a device"
                    :items="devices"
                    outlined
                    class="mt-4"
                    return-object
                    multiple
                    hide-selected
                    color="accent"
                    :item-text="v => `${v.name} (${v.healthCheckStatus})`"
                    :error-messages="deviceErrorMessage"
                >
                    <template v-slot:selection="{ item }">
                        <v-chip
                            small
                            color="primary"
                            close
                            @click:close="onRemoveSelectedDevice(item)"
                            >{{ item.name }} ({{
                                item.healthCheckStatus
                            }})</v-chip
                        >
                    </template>
                </v-select>
            </v-card-text>
            <v-card-actions class="px-5 pb-3">
                <v-container fluid pa-0>
                    <v-row no-gutters class="pb-1">
                        <v-col class="d-flex justify-start"></v-col>
                        <v-col class="d-flex justify-center">
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        color="purple accent-4"
                                        small
                                        icon
                                        :disabled="!allowFaveButtons"
                                        v-on="on"
                                    >
                                        <v-icon large>looks_two</v-icon>
                                    </v-btn>
                                </template>
                                <span>Programmable button 2</span>
                            </v-tooltip>
                        </v-col>
                        <v-col class="d-flex justify-end">
                            <v-btn
                                outlined
                                small
                                :disabled="!allowClose"
                                @click="onCloseClick"
                                >Close</v-btn
                            >
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col class="d-flex justify-start align-center">
                            <v-btn
                                small
                                color="accent"
                                :disabled="!allowDefaultChannel"
                                @click="onDefaultChannelClick"
                                >Default ({{ defaultChannel }})</v-btn
                            >
                        </v-col>
                        <v-col class="d-flex justify-center align-center">
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        color="green accent-4"
                                        small
                                        icon
                                        :disabled="!allowFaveButtons"
                                        v-on="on"
                                        ><v-icon large>looks_one</v-icon></v-btn
                                    >
                                </template>
                                <span>Programmable button 1</span>
                            </v-tooltip>
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        small
                                        fab
                                        color="accent"
                                        class="mx-3"
                                        :disabled="!allowFaveButtons"
                                        v-on="on"
                                        ><v-icon>{{ icons.mic }}</v-icon></v-btn
                                    >
                                </template>
                                <span
                                    >Voice command - Say something like: channel
                                    CNN</span
                                >
                            </v-tooltip>
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        color="orange accent-4"
                                        small
                                        icon
                                        :disabled="!allowFaveButtons"
                                        v-on="on"
                                        ><v-icon large>looks_3</v-icon></v-btn
                                    >
                                </template>
                                <span>Programmable button 3</span>
                            </v-tooltip>
                        </v-col>
                        <v-col class="d-flex justify-end align-center">
                            <v-btn
                                color="accent"
                                small
                                :disabled="!allowChangeChannel"
                                @click="onChangeChannel"
                                >Tune Into Channel</v-btn
                            >
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import ServiceAccountInfo from '@/views/spaces/components/ServiceAccountInfo';
import { mapActions, mapState } from 'vuex';
import moment from 'moment';
import { debounce } from 'debounce';

export default {
    name: 'TvListingsDialog',
    components: {
        'service-account-info': ServiceAccountInfo
    },
    props: {
        show: {
            type: Boolean,
            required: true
        },
        serviceAccount: {
            type: Object,
            required: true
        },
        channelMap: {
            type: Object,
            default: () => {
                'N/A';
            }
        },
        title: {
            type: String,
            required: false,
            default: 'Channel Listings'
        },
        width: {
            type: String,
            required: false,
            default: '550'
        }
    },
    data() {
        return {
            myriadBaseUrl:
                'https://edge.myriad-xcr.xcr.comcast.net/select/logo',
            myriadParams:
                'width=48&height=48&extent=true&selectionCriteria[0].colorPalette=color',
            clientProfile: 'XRE:X2',
            startTimeSlot: 'Time',
            channels: [],
            headers: [],
            isDataLoading: false,
            channelFilter: '',
            selectedChannel: null,
            selectedDevices: []
        };
    },
    computed: {
        ...mapState('channelMaps', ['channelMaps']),
        displayWidth() {
            if (this.$vuetify.breakpoint.mdAndUp) {
                return this.width;
            }
            return '90%';
        },
        displayTitle() {
            return `${this.title} (${this.filteredChannels.length})`;
        },
        devices() {
            if (
                this.serviceAccount &&
                this.serviceAccount.devices &&
                this.serviceAccount.devices.length
            ) {
                return this.serviceAccount.devices;
            }
            return [];
        },
        allowClose() {
            return !this.isDataLoading;
        },
        allowChangeChannel() {
            if (
                !this.isDataLoading &&
                this.selectedChannel &&
                this.isValidSelectedDevices
            ) {
                return true;
            }
            return false;
        },
        allowDefaultChannel() {
            return (
                !this.isDataLoading &&
                this.isValidSelectedDevices &&
                this.defaultChannel
            );
        },
        allowFaveButtons() {
            return !this.isDataLoading && this.isValidSelectedDevices;
        },
        deviceErrorMessage() {
            if (this.selectedDevices.length && !this.isValidSelectedDevices) {
                return 'The selected device status is not valid! Please select another device.';
            }
            return '';
        },
        filteredChannels() {
            if (this.channelFilter) {
                return this.channels.filter(
                    item =>
                        item.number.toString().startsWith(this.channelFilter) ||
                        (item.shortName &&
                            item.shortName
                                .toLowerCase()
                                .startsWith(this.channelFilter.toLowerCase()))
                );
            }
            return this.channels;
        },
        tableHeaders() {
            if (this.headers && this.headers.length) {
                return this.headers.map(item => {
                    return {
                        ...item,
                        start: moment(item.startTime).format('hh:mm'),
                        end: moment(item.endTime).format('hh:mm a')
                    };
                });
            }
            return [];
        },
        isValidSelectedDevices() {
            if (this.selectedDevices && this.selectedDevices.length) {
                return !this.selectedDevices.find(
                    item =>
                        item.healthCheckStatus &&
                        item.healthCheckStatus !== 'CONNECTED'
                );
            }
            return false;
        },
        defaultChannel() {
            if (
                this.channelMap.id &&
                this.channelMaps &&
                this.channelMaps.length
            ) {
                const selectedMap = this.channelMaps.find(
                    item => item.id === this.channelMap.id
                );
                if (selectedMap) {
                    return selectedMap.defaultChannel.toString();
                }
            }
            return '';
        }
    },
    mounted() {
        this.getGridData();
        setTimeout(() => {
            this.selectDefaultDevice(this.devices);
        }, 200);
    },
    methods: {
        ...mapActions('serviceAccount', ['fetchGrid']),
        ...mapActions('device', ['remoteTune']),
        onCloseClick() {
            this.$emit('close');
        },
        onEscapeKeydown() {
            this.onCloseClick();
        },
        onChannelSelectChange(selected, itemValue) {
            if (selected) {
                this.selectedChannel = itemValue;
            } else {
                this.selectedChannel = null;
            }
        },
        onChangeChannel() {
            this.isDataLoading = true;
            this.remoteTune({
                deviceIds: this.selectedDevices.map(device => device.id),
                channelNumber: this.selectedChannel
            }).finally(() => {
                this.isDataLoading = false;
            });
        },
        onDefaultChannelClick() {
            this.isDataLoading = true;
            this.remoteTune({
                deviceIds: this.selectedDevices.map(device => device.id),
                channelNumber: this.defaultChannel
            }).finally(() => {
                this.isDataLoading = false;
            });
        },
        onChannelFilterInput: debounce(function(value) {
            this.isDataLoading = true;
            this.channelFilter = value;
            setTimeout(() => {
                this.isDataLoading = false;
            }, 500);
        }, 600),
        onRemoveSelectedDevice(item) {
            if (this.selectedDevices.length && item) {
                this.selectedDevices = this.selectedDevices.filter(
                    device => device.id !== item.id
                );
            }
        },
        getGridData() {
            this.isDataLoading = true;
            const currentTime = moment();
            const nearestHalfHour = currentTime.minutes() >= 30 ? 30 : 0;
            const startDate = currentTime
                .minutes(nearestHalfHour)
                .seconds(0)
                .milliseconds(0)
                .toISOString()
                .slice(0, 16);
            this.fetchGrid({
                serviceAccountId: this.serviceAccount.xboAccountId,
                clientProfile: this.clientProfile,
                startDate: `${startDate}Z`
            })
                .then(res => {
                    if (res && res.data && res.data.headers.length) {
                        this.headers = res.data.headers.slice(0, 2);
                        const firstSlot = res.data.headers[0];
                        this.startTimeSlot = `${moment(
                            firstSlot.startTime
                        ).format('hh:mm')} - ${moment(firstSlot.endTime).format(
                            'hh:mm a'
                        )}`;
                    }
                    if (
                        res &&
                        res.data &&
                        res.data.channels &&
                        res.data.channels.length
                    ) {
                        this.calcDisplayListing(res.data.channels);
                        this.channels = res.data.channels;
                    }
                })
                .finally(() => {
                    this.isDataLoading = false;
                });
        },
        channelIcon(channelId) {
            const themeMode = this.$vuetify.theme.dark ? 'dark' : 'light';
            return `${this.myriadBaseUrl}?entityId=${channelId}&${this.myriadParams}&selectionCriteria[0].forUseOn=${themeMode}`;
        },
        calcDisplayListing(channels) {
            if (channels && channels.length) {
                for (const channel of channels) {
                    if (channel.listings && channel.listings.length) {
                        let remainingTime = 60;
                        for (const listing of channel.listings) {
                            if (remainingTime > 0) {
                                const displayTime =
                                    listing.inWindowMinutes <= remainingTime
                                        ? listing.inWindowMinutes
                                        : remainingTime;
                                listing.span = Math.floor(displayTime);
                                listing.display = true;
                                remainingTime -= listing.inWindowMinutes;
                            } else {
                                listing.display = false;
                            }
                        }
                    }
                }
            }
        },
        selectDefaultDevice(devices) {
            if (devices && devices.length) {
                devices.forEach(device => {
                    if (
                        device &&
                        device.healthCheckStatus &&
                        device.healthCheckStatus === 'CONNECTED'
                    ) {
                        this.selectedDevices = [device];
                    }
                });
            }
        }
    }
};
</script>

<style lang="scss">
.listings-table table {
    table-layout: fixed;

    th.select-col {
        width: 30px;
    }
    th.channel-col {
        width: 100px;
    }
    td.listing-col {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
}

@include theme(v-data-table) using ($material) {
    thead tr th.header-col {
        border-left: solid 1.5pt map-get($material, 'dividers') !important;
    }
    tbody tr td.listing-col {
        border-left: solid 1.5pt map-get($material, 'dividers') !important;
    }
}
.theme--light.v-chip:not(.v-chip--active) {
    color: map-get($shades, 'white') !important;
}
</style>
